import moment from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';
import { IAppointment, IAppointmentEvent } from '../../helpers/Interfaces';

import "./AppointmentTable.scss";

type ModalType = "add" | "edit" | "view";

type AppointmentTableProps = {
    dateRange: moment.Moment[];
    appointments: IAppointment[];
    setSelectedAppointment: (event: IAppointmentEvent) => void;
    openModal: (modal: ModalType) => void;
};

const timeToRowIndex = (timeStr: string | Date): number => {
    const m = moment(timeStr);
    const hours = m.hours();
    const minutes = m.minutes();
    return hours * 2 + (minutes >= 30 ? 2 : 1);
};

function AppointmentTable({ appointments, setSelectedAppointment, openModal }: AppointmentTableProps) {
    const calendarContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (calendarContainerRef.current) {
            const startRowElement = calendarContainerRef.current.querySelector('[data-hour="8"]');
            if (startRowElement) {
                startRowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, []);

    const hours = useMemo(() => {
        return Array.from({ length: 24 }, (_, hour) => ({
            hour,
            label: moment({ hour }).format('hh:00 A'),
        }));
    }, []);

    const formattedEvents = useMemo(() => {
        return appointments.flatMap((appointment, dayIndex) => {
            const colIndex = dayIndex + 2;

            return (appointment.events || []).map((event: IAppointmentEvent) => {
                const startRow = timeToRowIndex(event.startTime);
                const endRow = timeToRowIndex(event.endTime);
                const rowSpan = Math.max(endRow - startRow, 1);

                return { ...event, colIndex, startRow: startRow + 1, rowSpan, };
            });
        });
    }, [appointments]);

    return (
        <div className="appointment-grid-container" ref={calendarContainerRef}
            style={{ gridTemplateColumns: `80px repeat(${appointments.length}, minmax(180px, 1fr))` }}>
            <div className="grid-header-corner" />

            {appointments.map((item, index) => {
                const dateObj = moment.utc(item.appointmentDate).local();
                return (
                    <div key={item.appointmentDate?.toString() || index} className="grid-day-header"
                        style={{ gridColumn: index + 2 }}>
                        <span className="week-day">{dateObj.format("ddd - MMM")}</span>
                        <span className="date">{dateObj.format("Do")}</span>
                    </div>
                );
            })}

            {hours.map(({ hour, label }) => {
                const rowStart = hour * 2 + 2;
                return (
                    <div key={hour} data-hour={hour} className="grid-time-label" style={{ gridRow: `${rowStart} / span 2` }}>
                        {label}
                    </div>
                );
            })}

            {Array.from({ length: 48 }).map((_, index) => (
                <div key={index} className={`grid-background-slot ${index % 2 === 0 ? 'hour-start' : 'half-hour'}`}
                    style={{ gridRow: index + 2, gridColumn: `2 / span ${appointments.length}`, }}
                />
            ))}

            {formattedEvents.map((event) => {
                const isConfirmed = event.appointmentStatus?.toLowerCase() === 'confirmed';

                return (
                    <div key={event.appointmentId}
                        className={`grid-event-card ${isConfirmed ? 'is-confirmed' : ''}`}
                        style={{
                            gridColumn: event.colIndex,
                            gridRow: `${event.startRow} / span ${event.rowSpan}`,
                        }}
                        onClick={() => { setSelectedAppointment(event); openModal("view"); }}>
                        <div className="event-info">
                            <div className="clinic-name">{event.clinicName}</div>
                            <div className="clinic-time">
                                {`${moment(event.startTime).format("hh:mm A")} - ${moment(event.endTime).format("hh:mm A")}`}
                            </div>
                            <div className="patient-name">
                                {event.paperworkReceived && (
                                    <span title="Paperwork Received" style={{ marginRight: '4px' }}>📄</span>
                                )}
                                {event.patientName}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default AppointmentTable;